/**
 * Admin User Management & RBAC — Module 12.
 * Loads staff from Supabase (falls back to localStorage), supports adding,
 * role-changing, suspending/activating staff members.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { siteConfig } from "@/config/site";
import { Shield, Plus, Check, X, UserCheck, UserX, ChevronDown, Loader2 } from "lucide-react";
import {
  fetchAdminUsersFromSupabase,
  getStoredAdminUsers,
  createAdminUserInDb,
  updateAdminUserRoleInDb,
  toggleAdminUserStatusInDb,
  saveStoredAdminUsers,
  type AdminUserRecord,
  type AdminRole,
} from "@/features/admin/users/store/admin-users-store";

export const Route = createFileRoute("/admin/users")({
  head: () => ({
    meta: [{ title: `Staff & User Roles — Admin — ${siteConfig.name}` }],
  }),
  component: AdminUsersPage,
});

const ROLES: AdminRole[] = ["ADMIN", "STAFF"];

const ROLE_COLORS: Record<AdminRole, string> = {
  ADMIN: "bg-blue-100 text-blue-800 border border-blue-200",
  STAFF: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const ROLE_PERMISSIONS: Record<AdminRole, string[]> = {
  ADMIN: ["All permissions — full admin access"],
  STAFF: ["products.view", "orders.view", "customers.view"],
};

function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUserRecord[]>(() => getStoredAdminUsers());
  const [loading, setLoading] = useState(users.length === 0);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<AdminRole>("STAFF");
  const [inviting, setInviting] = useState(false);
  const [search, setSearch] = useState("");
  const [editingRole, setEditingRole] = useState<string | null>(null);

  const toast = (msg: string, type: "success" | "error" = "success") => {
    setToastMsg(msg);
    setToastType(type);
    setTimeout(() => setToastMsg(null), 3500);
  };

  const reload = async () => {
    setLoading(true);
    const data = await fetchAdminUsersFromSupabase();
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => {
    reload();
  }, []);

  // ── Invite new staff ────────────────────────────────────────────────────────
  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteName.trim()) return;
    setInviting(true);
    try {
      const newUser: AdminUserRecord = {
        id: `adm_${Date.now()}`,
        name: inviteName.trim(),
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        status: "ACTIVE",
        lastActive: "Just invited",
        permissions: ROLE_PERMISSIONS[inviteRole],
      };

      // Persist to Supabase Database; a failed write is reported instead of being
      // celebrated locally. The local list only records the invite once it persisted.
      const persistResult = await createAdminUserInDb(newUser);
      if (!persistResult.success) {
        toast(`Failed: ${persistResult.error || "could not save to database"}`, "error");
        return;
      }

      const updated = [
        newUser,
        ...users.filter((u) => u.email.toLowerCase() !== newUser.email.toLowerCase()),
      ];
      setUsers(updated);
      saveStoredAdminUsers(updated);
      toast(`✓ ${newUser.name} invited as ${inviteRole}`);

      setShowInvite(false);
      setInviteEmail("");
      setInviteName("");
      setInviteRole("STAFF");
    } catch (err: unknown) {
      const message = (err as { message?: string }).message;
      toast(`Failed: ${message}`, "error");
    } finally {
      setInviting(false);
    }
  };

  // ── Change role ─────────────────────────────────────────────────────────────
  const handleRoleChange = async (userId: string, newRole: AdminRole) => {
    const targetUser = users.find((u) => u.id === userId);
    const updated = users.map((u) => (u.id === userId ? { ...u, role: newRole } : u));
    setUsers(updated);
    saveStoredAdminUsers(updated);
    setEditingRole(null);

    if (targetUser) {
      await updateAdminUserRoleInDb(userId, targetUser.email, newRole);
    }
    toast(`Role updated to ${newRole}`);
  };

  // ── Toggle suspend / activate ────────────────────────────────────────────────
  const handleToggleStatus = async (userId: string, currentStatus: "ACTIVE" | "SUSPENDED") => {
    const newStatus = (currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE") as
      "ACTIVE" | "SUSPENDED";
    const targetUser = users.find((u) => u.id === userId);
    const updated = users.map((u) => (u.id === userId ? { ...u, status: newStatus } : u));
    setUsers(updated);
    saveStoredAdminUsers(updated);

    if (targetUser) {
      await toggleAdminUserStatusInDb(userId, targetUser.email, newStatus === "ACTIVE");
    }
    toast(`Staff member ${newStatus === "ACTIVE" ? "reactivated" : "suspended"}`);
  };

  const filtered = users.filter(
    (u) =>
      !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.role.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6 pb-16">
      {/* Toast */}
      {toastMsg && (
        <div
          className={`fixed top-20 right-6 z-50 text-xs font-semibold px-4 py-3 rounded-xl shadow-xl flex items-center gap-2 animate-in slide-in-from-right duration-200 ${
            toastType === "success" ? "bg-stone-900 text-white" : "bg-rose-700 text-white"
          }`}
        >
          <Check className="h-4 w-4 text-emerald-400 shrink-0" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-bold uppercase tracking-widest">
            Role-Based Access Control (RBAC)
          </p>
          <h1 className="mt-1 font-display text-3xl text-foreground">Staff & User Roles</h1>
          <p className="mt-1 text-xs text-muted-foreground max-w-lg">
            Add staff members, assign roles, and control what each team member can access in the
            admin console.
          </p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="flex shrink-0 items-center gap-2 rounded-xl bg-stone-900 px-4 py-2.5 text-xs font-bold text-white uppercase tracking-wider hover:bg-stone-800 transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" />
          Invite Staff
        </button>
      </div>

      {/* Role legend */}
      <div className="flex flex-wrap gap-2">
        {ROLES.map((role) => (
          <span
            key={role}
            className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${ROLE_COLORS[role]}`}
          >
            {role.replace(/_/g, " ")}
          </span>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by name, email or role…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-sm rounded-xl border border-border bg-background px-4 py-2.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
      />

      {/* Table */}
      <div className="rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 font-bold text-muted-foreground uppercase tracking-wider text-[10px]">
                Staff Member
              </th>
              <th className="text-left px-4 py-3 font-bold text-muted-foreground uppercase tracking-wider text-[10px]">
                Role
              </th>
              <th className="text-left px-4 py-3 font-bold text-muted-foreground uppercase tracking-wider text-[10px]">
                Permissions
              </th>
              <th className="text-left px-4 py-3 font-bold text-muted-foreground uppercase tracking-wider text-[10px]">
                Last Active
              </th>
              <th className="text-left px-4 py-3 font-bold text-muted-foreground uppercase tracking-wider text-[10px]">
                Status
              </th>
              <th className="text-right px-4 py-3 font-bold text-muted-foreground uppercase tracking-wider text-[10px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-background">
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-16 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading staff…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-16 text-muted-foreground">
                  <Shield className="h-8 w-8 mx-auto mb-3 opacity-30" />
                  <p className="font-semibold text-sm">No staff members yet</p>
                  <p className="text-xs mt-1 opacity-70">
                    Click "Invite Staff" to add your first team member.
                  </p>
                </td>
              </tr>
            ) : (
              filtered.map((user) => (
                <tr key={user.id} className="hover:bg-stone-50/50 transition-colors">
                  {/* Staff member */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{user.name}</p>
                        <p className="text-muted-foreground font-mono text-[11px]">{user.email}</p>
                      </div>
                    </div>
                  </td>

                  {/* Role — click to edit */}
                  <td className="px-4 py-3">
                    {editingRole === user.id ? (
                      <div className="relative">
                        <select
                          value={user.role}
                          autoFocus
                          onChange={(e) => handleRoleChange(user.id, e.target.value as AdminRole)}
                          onBlur={() => setEditingRole(null)}
                          className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-primary/30 pr-6"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r.replace(/_/g, " ")}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingRole(user.id)}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold hover:opacity-80 transition-opacity ${ROLE_COLORS[user.role]}`}
                        title="Click to change role"
                      >
                        {user.role.replace(/_/g, " ")}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    )}
                  </td>

                  {/* Permissions */}
                  <td className="px-4 py-3 max-w-[200px]">
                    <p className="text-muted-foreground text-[11px] leading-relaxed">
                      {(ROLE_PERMISSIONS[user.role] ?? user.permissions ?? [])
                        .slice(0, 3)
                        .join(", ")}
                      {(ROLE_PERMISSIONS[user.role] ?? user.permissions ?? []).length > 3 && "…"}
                    </p>
                  </td>

                  {/* Last active */}
                  <td className="px-4 py-3">
                    <span className="font-mono text-muted-foreground text-[11px]">
                      {user.lastActive}
                    </span>
                  </td>

                  {/* Status badge */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                        user.status === "ACTIVE"
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                          : "bg-rose-100 text-rose-800 border border-rose-200"
                      }`}
                    >
                      {user.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleToggleStatus(user.id, user.status)}
                      title={user.status === "ACTIVE" ? "Suspend access" : "Restore access"}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide border transition-colors ${
                        user.status === "ACTIVE"
                          ? "border-rose-200 text-rose-700 hover:bg-rose-50"
                          : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      }`}
                    >
                      {user.status === "ACTIVE" ? (
                        <>
                          <UserX className="h-3.5 w-3.5" /> Suspend
                        </>
                      ) : (
                        <>
                          <UserCheck className="h-3.5 w-3.5" /> Activate
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Showing {filtered.length} of {users.length} staff member{users.length !== 1 ? "s" : ""}
      </p>

      {/* ── Invite Modal ─────────────────────────────────────────────────────── */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl bg-background border border-border shadow-2xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-xl text-foreground">
                Invite Staff Member
              </h2>
              <button
                onClick={() => setShowInvite(false)}
                className="rounded-full p-2 hover:bg-stone-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Full Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Priya Sharma"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="w-full rounded-xl border border-border bg-stone-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="priya@asterdecorz.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full rounded-xl border border-border bg-stone-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as AdminRole)}
                  className="w-full rounded-xl border border-border bg-stone-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>

                {/* Permission preview */}
                <div className="mt-2 rounded-xl bg-stone-50 border border-stone-200 p-3">
                  <p className="text-[10px] font-bold uppercase text-muted-foreground mb-1">
                    Permissions granted:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {ROLE_PERMISSIONS[inviteRole].map((perm) => (
                      <span
                        key={perm}
                        className="rounded-full bg-white border border-stone-200 px-2 py-0.5 text-[10px] font-mono text-stone-700"
                      >
                        {perm}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowInvite(false)}
                className="flex-1 rounded-xl border border-border py-2.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim() || !inviteName.trim()}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-stone-900 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                {inviting ? "Inviting…" : "Send Invite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
